import { Dispatch, SetStateAction, useState } from 'react';
import { Alert, Divider, Stack, Typography } from '@mui/material';
import { mapMembershipRole, MembershipRole } from 'tdc-web-backend/enums/enums';
import { CreateUserInvitationDto } from 'tdc-web-backend/user-invitation/schemas';
import ErrorIcon from '@mui/icons-material/Error';
import { useTranslation } from 'react-i18next';
import ConfirmModal from '../../../../../../components/confirm-modal/ConfirmModal';
import useCreate from '../../../../../../utils/hooks/crud-hooks/useCreate';
import { ReactComponent as ClipboardCheckIcon } from '../../../../../../assets/icons/layout-icons/ClipboardCheckIconEditModal.svg';
import { primaryLight } from '../../../../../../utils/color';

export type ResendModalData = {
  email: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  role: MembershipRole;
};

type ResendModalProps = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onSuccessfulSubmit?: () => void;
  data: ResendModalData;
};

const ResendModal = ({ isOpen, setIsOpen, onSuccessfulSubmit, data }: ResendModalProps) => {
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const { mutate, isLoading } = useCreate<{}, CreateUserInvitationDto>({
    resource: 'user-invitation',
  });

  const onSubmit = () => {
    mutate(
      {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        jobTitle: data.jobTitle,
        role: data.role,
      },
      {
        onSuccess: () => {
          setError(null);
          setIsOpen(false);
          onSuccessfulSubmit?.();
        },
        onError: (err) => {
          setError(err.response?.data.message || err.message);
        },
      },
    );
  };

  return (
    <ConfirmModal
      isModalOpen={isOpen}
      automaticallyCloseModalOnButtonClick={false}
      onCancel={() => setIsOpen(false)}
      onConfirm={onSubmit}
      setIsModalOpen={() => null}
      title={t('settings.members.resendInvitation')}
      width="900px"
      confirmButtonText={t('common.resend')}
      iconProps={{
        icon: <ClipboardCheckIcon fill={primaryLight[500]} />,
        wrapperBackgroundColor: '#E3DBFC',
      }}
      isLoading={isLoading}
      showCancelButton={!isLoading}
      message={
        <>
          <Stack spacing={3}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <Stack
              direction="row"
              spacing={3}
              justifyContent="cener"
              divider={<Divider orientation="vertical" flexItem />}
            >
              <Stack width="50%" spacing={3} justifyContent="space-between">
                <Stack>
                  <Typography variant="heading5" color="primaryDark.500">
                    {t('common.email')}
                  </Typography>
                  <Typography variant="bodyMedium3" color="primaryDark.800">
                    {data.email}
                  </Typography>
                </Stack>
                <Stack>
                  <Typography variant="heading5" color="primaryDark.500">
                    {t('common.jobTitle')}
                  </Typography>
                  <Typography variant="bodyMedium3" color="primaryDark.800">
                    {data.jobTitle}
                  </Typography>
                </Stack>
              </Stack>
              <Stack width="50%" spacing={3} justifyContent="space-between">
                <Stack>
                  <Typography variant="heading5" color="primaryDark.500">
                    {t('common.firstName')}
                  </Typography>
                  <Typography variant="bodyMedium3" color="primaryDark.800">
                    {data.firstName}
                  </Typography>
                </Stack>
                <Stack>
                  <Typography variant="heading5" color="primaryDark.500">
                    {t('common.lastName')}
                  </Typography>
                  <Typography variant="bodyMedium3" color="primaryDark.800">
                    {data.lastName}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
            <Divider />
            <Stack spacing={2.5}>
              <Stack spacing={0}>
                <Typography variant="heading4" color="primaryDark.500">
                  {t('settings.members.role')}
                  {'  '}
                </Typography>
                <Typography variant="bodyMedium3" color="primaryDark.800" component="span">
                  {mapMembershipRole.get(data.role)}
                </Typography>
              </Stack>
              <Typography variant="body3" color="primaryDark.700">
                {data.role === MembershipRole.Admin
                  ? t('settings.members.adminDescription')
                  : t('settings.members.editorDescription')}
              </Typography>
            </Stack>
          </Stack>
        </>
      }
    />
  );
};

export default ResendModal;
